import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RichTextEditor } from "./RichTextEditor";
import { 
  Mail, 
  Phone, 
  Instagram, 
  Facebook, 
  Youtube, 
  MapPin, 
  Building2,
  Save,
  Loader2,
  Music2,
  Signature,
  Bot
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

interface BusinessSetting {
  id: string;
  key: string;
  value: string;
  label: string;
  category: string;
  sort_order: number;
}

const categoryConfig = {
  contact: {
    title: "Contact Information",
    icon: Mail,
    description: "Email and phone details shown across the site",
  },
  social: {
    title: "Social Media Links",
    icon: Instagram,
    description: "Links to your social media profiles",
  },
  location: {
    title: "Location & Service Areas",
    icon: MapPin,
    description: "Where you provide services",
  },
  branding: {
    title: "Branding",
    icon: Building2,
    description: "Business name and tagline",
  },
  email: {
    title: "Email Settings",
    icon: Signature,
    description: "Configure your email signature for outgoing messages",
  },
  ai: {
    title: "AI Settings",
    icon: Bot,
    description: "Customize how AI generates email replies",
  },
};

const iconMap: Record<string, React.ElementType> = {
  email: Mail,
  phone: Phone,
  phone_display: Phone,
  instagram_url: Instagram,
  facebook_url: Facebook,
  youtube_url: Youtube,
  tiktok_url: Music2,
  service_areas: MapPin,
  business_name: Building2,
  tagline: Building2,
  email_signature: Signature,
  ai_email_prompt: Bot,
};

// Settings that use rich text editor instead of plain input
const richTextSettings = ["email_signature"];

export default function BusinessSettingsManager() {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ["business-settings-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_settings")
        .select("*")
        .order("category")
        .order("sort_order");

      if (error) throw error;
      return data as BusinessSetting[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: string }) => {
      const { error } = await supabase
        .from("business_settings")
        .update({ value })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-settings-admin"] });
      queryClient.invalidateQueries({ queryKey: ["business-settings"] });
      toast.success("Setting updated successfully");
    },
    onError: (error) => {
      console.error("Error updating setting:", error);
      toast.error("Failed to update setting");
    },
  });

  const handleSave = async (setting: BusinessSetting) => {
    const newValue = editedValues[setting.key];
    if (newValue === undefined || newValue === setting.value) return;

    setSavingKeys((prev) => new Set(prev).add(setting.key));
    
    try {
      await updateMutation.mutateAsync({ id: setting.id, value: newValue });
      setEditedValues((prev) => {
        const updated = { ...prev };
        delete updated[setting.key];
        return updated;
      });
    } finally {
      setSavingKeys((prev) => {
        const updated = new Set(prev);
        updated.delete(setting.key);
        return updated;
      });
    }
  };

  const handleInputChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const getDisplayValue = (setting: BusinessSetting) => {
    return editedValues[setting.key] ?? setting.value;
  };

  const hasChanges = (setting: BusinessSetting) => {
    const editedValue = editedValues[setting.key];
    return editedValue !== undefined && editedValue !== setting.value;
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {} as Record<string, BusinessSetting[]>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const categories = ["contact", "social", "location", "branding", "email", "ai"];

  // Settings that use a plain textarea (multi-line but not rich text)
  const textareaSettings = ["ai_email_prompt"];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Business Settings</h2>
        <p className="text-muted-foreground mt-1">
          Manage your business details that appear throughout the website
        </p>
      </div>

      {categories.map((category) => {
        const config = categoryConfig[category as keyof typeof categoryConfig];
        const categorySettings = groupedSettings[category] || [];
        const Icon = config.icon;

        if (categorySettings.length === 0) return null;

        return (
          <Card key={category}>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{config.title}</CardTitle>
                  <p className="text-sm text-muted-foreground">{config.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {categorySettings.map((setting) => {
                const SettingIcon = iconMap[setting.key] || Building2;
                const isSaving = savingKeys.has(setting.key);
                const changed = hasChanges(setting);

                return (
                  <div key={setting.id} className="space-y-2">
                    <div className="flex items-end gap-3">
                      <div className="flex-1 space-y-2">
                        <Label htmlFor={setting.key} className="flex items-center gap-2">
                          <SettingIcon className="h-4 w-4 text-muted-foreground" />
                          {setting.label}
                        </Label>
                        {richTextSettings.includes(setting.key) ? (
                          <div className={changed ? "ring-1 ring-primary rounded-md" : ""}>
                            <RichTextEditor
                              value={getDisplayValue(setting)}
                              onChange={(value) => handleInputChange(setting.key, value)}
                              placeholder={`Enter ${setting.label.toLowerCase()}`}
                            />
                          </div>
                        ) : textareaSettings.includes(setting.key) ? (
                          <Textarea
                            id={setting.key}
                            value={getDisplayValue(setting)}
                            onChange={(e) => handleInputChange(setting.key, e.target.value)}
                            placeholder={`Enter ${setting.label.toLowerCase()}`}
                            className={`min-h-[200px] font-mono text-sm ${changed ? "border-primary" : ""}`}
                            rows={12}
                          />
                        ) : (
                          <Input
                            id={setting.key}
                            value={getDisplayValue(setting)}
                            onChange={(e) => handleInputChange(setting.key, e.target.value)}
                            placeholder={`Enter ${setting.label.toLowerCase()}`}
                            className={changed ? "border-primary" : ""}
                          />
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSave(setting)}
                        disabled={!changed || isSaving}
                        className="shrink-0"
                      >
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        <span className="ml-2">Save</span>
                      </Button>
                    </div>
                    {setting.key === "email_signature" && (
                      <p className="text-xs text-muted-foreground pl-6">
                        This signature will be automatically appended to all outgoing emails.
                      </p>
                    )}
                    {setting.key === "ai_email_prompt" && (
                      <p className="text-xs text-muted-foreground pl-6">
                        The AI will also have access to your knowledge base, pricing packages, and conversation history when generating replies.
                      </p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
